import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function InspirationalWomenManager() {
  const [women, setWomen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWoman, setEditingWoman] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameMarathi: '',
    photo: '',
    work: '',
    workMarathi: '',
    description: '',
    descriptionMarathi: '',
    achievements: '',
    achievementsMarathi: '',
    order: 1,
  });

  useEffect(() => {
    loadWomen();
  }, []);

  const loadWomen = async () => {
    try {
      const q = query(collection(db, 'inspirationalWomen'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const womenData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setWomen(womenData);
    } catch (error) {
      console.error('Error loading inspirational women:', error);
      alert('Failed to load inspirational women');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingWoman) {
        await updateDoc(doc(db, 'inspirationalWomen', editingWoman.id), {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
        alert('Inspirational woman updated successfully!');
      } else {
        await addDoc(collection(db, 'inspirationalWomen'), {
          ...formData,
          createdAt: new Date().toISOString(),
        });
        alert('Inspirational woman added successfully!');
      }
      setShowModal(false);
      setEditingWoman(null);
      resetForm();
      loadWomen();
    } catch (error) {
      console.error('Error saving inspirational woman:', error);
      alert('Failed to save inspirational woman');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (woman) => {
    setEditingWoman(woman);
    setFormData({
      name: woman.name,
      nameMarathi: woman.nameMarathi || '',
      photo: woman.photo || '',
      work: woman.work,
      workMarathi: woman.workMarathi || '',
      description: woman.description,
      descriptionMarathi: woman.descriptionMarathi || '',
      achievements: woman.achievements,
      achievementsMarathi: woman.achievementsMarathi || '',
      order: woman.order,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inspirational woman?')) return;

    try {
      await deleteDoc(doc(db, 'inspirationalWomen', id));
      alert('Inspirational woman deleted successfully!');
      loadWomen();
    } catch (error) {
      console.error('Error deleting inspirational woman:', error);
      alert('Failed to delete inspirational woman');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      nameMarathi: '',
      photo: '',
      work: '',
      workMarathi: '',
      description: '',
      descriptionMarathi: '',
      achievements: '',
      achievementsMarathi: '',
      order: 1,
    });
  };

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Inspirational Women Management</h2>
            <button
              onClick={() => {
                setEditingWoman(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Add Inspirational Woman
            </button>
          </div>

          {loading && <p>Loading...</p>}

          {!loading && women.length === 0 && (
            <p>No inspirational women found. Add your first profile!</p>
          )}

          {!loading && women.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Work</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {women.map((woman) => (
                  <tr key={woman.id}>
                    <td>{woman.order}</td>
                    <td>
                      {woman.photo && (
                        <img
                          src={woman.photo}
                          alt={woman.name}
                          style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      )}
                    </td>
                    <td>
                      <div>{woman.name}</div>
                      {woman.nameMarathi && (
                        <div style={{ fontSize: '0.9em', color: '#666' }}>{woman.nameMarathi}</div>
                      )}
                    </td>
                    <td>{woman.work}</td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(woman)}
                        className="btn btn-secondary"
                        style={{ marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(woman.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingWoman ? 'Edit Inspirational Woman' : 'Add Inspirational Woman'}
              </h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name (English) *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter name in English"
                  required
                />
              </div>

              <div className="form-group">
                <label>Name (Marathi)</label>
                <input
                  type="text"
                  value={formData.nameMarathi}
                  onChange={(e) => setFormData({ ...formData, nameMarathi: e.target.value })}
                  placeholder="Enter name in Marathi"
                />
              </div>

              <div className="form-group">
                <label>Photo URL *</label>
                <input
                  type="url"
                  value={formData.photo}
                  onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                  placeholder="https://example.com/photo.jpg"
                  required
                />
                {formData.photo && (
                  <div style={{ marginTop: '10px' }}>
                    <img
                      src={formData.photo}
                      alt="Preview"
                      style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover' }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Work Description (English) *</label>
                <input
                  type="text"
                  value={formData.work}
                  onChange={(e) => setFormData({ ...formData, work: e.target.value })}
                  placeholder="e.g., First woman IPS officer"
                  required
                />
              </div>

              <div className="form-group">
                <label>Work Description (Marathi)</label>
                <input
                  type="text"
                  value={formData.workMarathi}
                  onChange={(e) => setFormData({ ...formData, workMarathi: e.target.value })}
                  placeholder="Enter work description in Marathi"
                />
              </div>

              <div className="form-group">
                <label>Full Description (English) *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter detailed biography and achievements"
                  required
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Full Description (Marathi)</label>
                <textarea
                  value={formData.descriptionMarathi}
                  onChange={(e) => setFormData({ ...formData, descriptionMarathi: e.target.value })}
                  placeholder="Enter detailed biography in Marathi"
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Achievements (English) *</label>
                <textarea
                  value={formData.achievements}
                  onChange={(e) => setFormData({ ...formData, achievements: e.target.value })}
                  placeholder="e.g., Padma Bhushan (2013), Olympic Medal (2012)"
                  required
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Achievements (Marathi)</label>
                <textarea
                  value={formData.achievementsMarathi}
                  onChange={(e) => setFormData({ ...formData, achievementsMarathi: e.target.value })}
                  placeholder="Enter achievements in Marathi"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingWoman ? 'Update Profile' : 'Add Profile'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingWoman(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default InspirationalWomenManager;
